import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaArrowTurnUp } from "react-icons/fa6";
import { FaDownload } from "react-icons/fa6";
import { GiCrossedSwords } from "react-icons/gi";
import { GoHeartFill } from "react-icons/go";
import { RiDeleteBin6Line } from "react-icons/ri";
import { LuRefreshCcw } from "react-icons/lu";
import useAsyncEffect from "use-async-effect";
import { Comments } from "../Comments/Comments";
import { AppContext } from "../State/Context";
import { ActionType, BattleState } from "../State/State";
import { askAPI, RequestMethod, showSystemMessage } from "../api/api";
import { requestHexagenInfo } from "../api/hexagen";
import { Icon } from "../components/Icon";
import { Hexagen, Player } from "../types";
import { getTimeAgo } from "../utils/misc";
import { imgFromCoords, isTouchDevice, rootURL, videoFromCoords } from "../utils/settings";
import { findNeighboursNumber } from "../World/Hexagon";
import { RiVideoAddLine } from "react-icons/ri";
import "./Card.scss";
import { CardImage } from "./CardImage";
import { GeneratingButton } from "../components/GeneratingButton";

export const MIN_DECK_SIZE = 24;
export const MAX_DECK_SIZE = 32;

type CardProps = {
  selectedHexCoord: { x: number; y: number };
  hexagenInfo: Hexagen | null;
  hexagenExtra?: {
    hasUpvote: boolean;
    creator: Player | null;
    owner: Player | null;
  } | null;
  onCloseHandler?: () => void;
  setSparks: (value: boolean) => void;
  /** When used in popup: positioning + animation for the single popup div */
  className?: string;
  style?: React.CSSProperties;
};

type AttackData = {
  chanceToWin: number;
  playerDeckRarityScore: number;
  playerDeckSize: number;
  capturedCardRarity: number;
  removedCard: Hexagen | null;
  isReasonable: boolean;
  newMediumRarityScore?: number;
  improvement?: number;
  improvementPercent?: string;
  isImproved?: boolean;
};

const MOUSE_EFFECT = {
  imageOffset: 30,
  rotation: 4,
};

// Store last cursor position so card can use it as initial tilt when popup opens
let lastMousePosition = { x: 0, y: 0 };
if (typeof document !== "undefined") {
  document.addEventListener(
    "mousemove",
    (e: MouseEvent) => {
      lastMousePosition.x = e.clientX;
      lastMousePosition.y = e.clientY;
    },
    { passive: true }
  );
}

export const Card: React.FC<CardProps> = ({
  selectedHexCoord,
  hexagenInfo,
  hexagenExtra,
  onCloseHandler,
  setSparks,
  className = "",
  style,
}) => {
  const { x, y } = selectedHexCoord;
  const loading = !hexagenInfo;
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasUpvote, setHasUpvote] = useState<boolean>(false);
  const [upvoteAnimation, setUpvoteAnimation] = useState<boolean>(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState<boolean>(false);
  const [attackData, setAttackData] = useState<AttackData | null>(null);
  const [showVideo, setShowVideo] = useState<boolean>(true);
  const [isAnimateOpen, setIsAnimateOpen] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [isRegeneratingVideo, setIsRegeneratingVideo] = useState<boolean>(false);
  const [promptVisibleLength, setPromptVisibleLength] = useState(0);
  const [isPopupVideoPlaying, setIsPopupVideoPlaying] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { dispatch, state } = useContext(AppContext);

  useEffect(() => {
    setShowVideo(true);
  }, [x, y]);

  useEffect(() => {
    setIsPopupVideoPlaying(false);
  }, [x, y, showVideo]);

  const promptText = hexagenInfo?.prompt?.trim() ?? "";
  useEffect(() => {
    if (!promptText) {
      setPromptVisibleLength(0);
      return;
    }
    setPromptVisibleLength(0);
    const total = promptText.length;
    const msPerChar = 28;
    const t = setInterval(() => {
      setPromptVisibleLength((n) => {
        if (n >= total) {
          clearInterval(t);
          return total;
        }
        return Math.min(n + 1, total);
      });
    }, msPerChar);
    return () => clearInterval(t);
  }, [promptText]);

  const isPlayerOwner = hexagenInfo?.owner === state.player?.uid;
  const videoCost = state.player?.role === "admin" ? 0 : 10;
  const canAffordVideo = videoCost === 0 || (state.player?.sparks || 0) >= videoCost;
  const enable3DEffect = true;
  const enableCardTilt = false;
  const deckSize = state.player?.deckSize || Infinity;
  const isDeckFull = deckSize >= MAX_DECK_SIZE;
  const showAddToDeckButton =
    !loading && !!hexagenInfo && isPlayerOwner && !isDeckFull && !hexagenInfo.isInDeck;

  useAsyncEffect(async () => {
    if (!hexagenInfo) return;
    if (hexagenInfo.creator === state.player?.uid) {
      setHasUpvote(true);
    }
  }, [hexagenInfo]);

  const clickOnCloseAttackData = () => {
    setAttackData(null);
  };

  const deleteHex = async () => {
    if (!hexagenInfo) return;
    if (window.confirm("Are you sure you want to delete this hexagen?")) {
      await askAPI<Hexagen>("/api/hexagen/delete", RequestMethod.POST, {
        x: hexagenInfo.x,
        y: hexagenInfo.y,
      });

      onCloseHandler?.();
      window.location.reload();
    }
  };

  const regenerateHex = async () => {
    if (!hexagenInfo) return;
    const prompt = hexagenInfo.prompt?.trim() ?? "";
    if (!prompt || prompt.length < 3) {
      showSystemMessage({ systemMessage: "error", text: "No prompt to regenerate with" });
      return;
    }
    setIsRegenerating(true);
    try {
      const res = await askAPI<boolean>(
        "/api/hexagen/regenerate",
        RequestMethod.POST,
        { x: hexagenInfo.x, y: hexagenInfo.y, prompt }
      );
      if (res === true) {
        showSystemMessage({ systemMessage: "success", text: "Hexagon regenerated." });
        onCloseHandler?.();
        requestHexagenInfo(hexagenInfo.x, hexagenInfo.y);
      }
    } catch (e) {
      showSystemMessage({ systemMessage: "error", text: "Regeneration request failed" });
    } finally {
      setIsRegenerating(false);
    }
  };

  const regenerateVideo = async () => {
    if (!hexagenInfo) return;
    setIsRegeneratingVideo(true);
    try {
      const res = await askAPI<{ ok?: boolean; error?: string; status?: string }>(
        "/api/hexagen/video/generate",
        RequestMethod.POST,
        { x: hexagenInfo.x, y: hexagenInfo.y }
      );
      if (res?.ok) {
        showSystemMessage({ systemMessage: "success", text: "Video generation started" });
        requestHexagenInfo(hexagenInfo.x, hexagenInfo.y);
      } else {
        showSystemMessage({ systemMessage: "error", text: res?.error ?? "Video generation failed" });
      }
    } catch (e) {
      showSystemMessage({ systemMessage: "error", text: "Video generation request failed" });
    } finally {
      setIsRegeneratingVideo(false);
    }
  };

  const clickOnConfirmAttack = async () => {
    if (!hexagenInfo || !state.player || !hexagenExtra?.owner) {
      console.error("Player or owner not found");
      return;
    }

    const battleState: BattleState = {
      uid: null,
      result: null,
      story: null,
      attacking: {
        hand: null,
        player: state.player.uid,
        main: null,
        secondary: null,
        third: null,
      },
      defending: {
        hand: null,
        player: hexagenExtra?.owner.uid,
        main: hexagenInfo.uid,
        secondary: null,
        third: null,
      },
    };
    dispatch({ type: ActionType.SHOW_BATTLE_FIELD_POPUP });
    dispatch({ type: ActionType.SET_BATTLE_STATE, battleState });
    onCloseHandler?.();
  };


  const updateTiltFromMouse = (clientX: number, clientY: number) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const halfW = rect.width / 2;
    const halfH = rect.height / 2;
    let normalizedX = halfW ? (clientX - centerX) / halfW : 0;
    let normalizedY = halfH ? (clientY - centerY) / halfH : 0;
    normalizedX = Math.max(-1, Math.min(1, normalizedX));
    normalizedY = Math.max(-1, Math.min(1, normalizedY));

    setImageOffset({
      x: -normalizedX * MOUSE_EFFECT.imageOffset,
      y: -normalizedY * MOUSE_EFFECT.imageOffset,
    });
    setRotation({
      x: -normalizedY * MOUSE_EFFECT.rotation,
      y: normalizedX * MOUSE_EFFECT.rotation,
    });
  };

  useLayoutEffect(() => {
    if (!enable3DEffect) return;
    updateTiltFromMouse(lastMousePosition.x, lastMousePosition.y);
  }, [enable3DEffect]);

  useEffect(() => {
    if (!enable3DEffect) return;
    const onGlobalMouseMove = (e: MouseEvent) => updateTiltFromMouse(e.clientX, e.clientY);
    document.addEventListener("mousemove", onGlobalMouseMove, { passive: true });
    return () => document.removeEventListener("mousemove", onGlobalMouseMove);
  }, [enable3DEffect]);

  const clickOnCreator = async () => {
    if (!hexagenInfo) return;
    const uid = hexagenInfo.creator;
    if (uid) {
      dispatch({ type: ActionType.SELECT_PLAYER, uid });
      dispatch({ type: ActionType.CLOSE_HEX_INFO_POPUP });
      dispatch({ type: ActionType.TOGGLE_PLAYER_INFO_POPUP });
    }
  };
  const clickOnOwner = async () => {
    if (!hexagenInfo) return;
    const uid = hexagenInfo.owner;
    if (uid) {
      dispatch({ type: ActionType.SELECT_PLAYER, uid });
      dispatch({ type: ActionType.CLOSE_HEX_INFO_POPUP });
      dispatch({ type: ActionType.TOGGLE_PLAYER_INFO_POPUP });
    }
  };

  const cardName = hexagenInfo?.headline?.replace(/\.$/, "") ?? "";
  const videoStatus = hexagenInfo?.videoStatus || "NONE";
  const neighboursCount = findNeighboursNumber(x, y, state.booleanMap);
  const hasAllNeighbors = neighboursCount >= 6;

  const flipCard = () => {
    console.log("flipCard");
    setIsFlipped(!isFlipped);
  };

  const handleDownload = async () => {
    const isVideoReady = videoStatus === "READY";
    const shouldDownloadVideo = isVideoReady && showVideo;

    if (shouldDownloadVideo) {
      const url = videoFromCoords(x, y, hexagenInfo?.videoVersion ?? undefined);
      try {
        const res = await fetch(url, { mode: "cors" });
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `hex-${x}-${y}.mp4`;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(url, "_blank");
      }
      return;
    }

    const url = `${rootURL}/share/compose/?x=${x}&y=${y}&r=0&c=s&f=jpg&q=80`;
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `hex-${x}-${y}.jpg`;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  const addToDeck = async () => {
    if (!hexagenInfo) return;
    const player = state.player;
    if (!player) return;
    await askAPI<Hexagen>("/api/hexagen/add-to-deck", RequestMethod.POST, {
      x: hexagenInfo.x,
      y: hexagenInfo.y,
    });

    dispatch({ type: ActionType.CLOSE_HEX_INFO_POPUP });
    requestAnimationFrame(() => {
      dispatch({ type: ActionType.OPEN_HEX_INFO_POPUP });
    });
  };

  const handleAnimate = async () => {
    if (!hexagenInfo) return;
    if (!hasAllNeighbors) {
      showSystemMessage({ systemMessage: "error", text: "All 6 neighbors required to animate this hexagon." });
      return;
    }
    setIsAnimateOpen(true);
  };

  const handleConfirmAnimate = async () => {
    if (!hexagenInfo) return;
    if (!state.player) {
      dispatch({ type: ActionType.TOGGLE_LOGIN_POPUP });
      return;
    }
    if (!canAffordVideo) {
      dispatch({ type: ActionType.TOGGLE_REDEEM_POPUP });
      return;
    }
    const { startVideoGeneration } = await import("../api/hexagen");
    const res = await startVideoGeneration(x, y);
    if (res) {
      setIsAnimateOpen(false);
    }
  };

  const handleUpvote = async () => {
    if (!hexagenInfo) return;
    const player = state.player;
    if (!player) return;
    const isSuccess = await askAPI<Hexagen>("/api/upvotes/", RequestMethod.GET, { x, y });

    if (isSuccess) {
      setHasUpvote(true);
      setUpvoteAnimation(true);
      setTimeout(() => {
        setUpvoteAnimation(false);
      }, 2000);
      requestHexagenInfo(x, y);
    }
  };

  const clickOnOpenComments = () => {
    setIsCommentsOpen(true);
  };

  const clickOnCloseComments = () => {
    setIsCommentsOpen(false);
  };

  const CloseButton = () => (
    <div
      onMouseDown={onCloseHandler}
      className={`bg-black absolute right-[0] top-[0] px-[12px] py-[3px] select-none cursor-pointer text-[22px] z-[1000000000]`}
    >
      ✕
      <div className="absolute right-[-50%] top-[-50%] w-[150%] h-[150%] bg-opacity-0"></div>
    </div>
  );

  const FlipButton = () => (
    <div
      onMouseDown={flipCard}
      className="bg-black absolute right-[5px] bottom-[3px] px-[6px] py-[3px] select-none cursor-pointer text-[22px] z-[1000000002]"
    >
      <Icon
        icon={FaArrowTurnUp}
        className={`text-[15px] inline-block ml-4 mr-[2px]`}
      />
      <div className="absolute right-[-40%] bottom-[-40%] w-[130%] h-[150%] bg-red-500 bg-opacity-0"></div>
    </div>
  );

  const cardSideStyles = {
    backfaceVisibility: "hidden" as const,
    WebkitBackfaceVisibility: "hidden" as const,
    transform: enableCardTilt
      ? `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
      : "translateZ(0)",
    transition: enable3DEffect ? "transform 150ms ease-out" : undefined,
  } as const;

  return (
    <div
      className={`${className} w-full max-w-[420px] aspect-[2/3.2] relative text-orange-100`.trim()}
      style={{ ...style, perspective: "1000px", transformStyle: "preserve-3d" }}
    >
      {isTouchDevice() && (
        <>
          <div
            onClick={onCloseHandler}
            className="absolute right-[0] top-[0] w-[50px] h-[50px] z-[200] cursor-pointer"
          />
          <div
            onClick={flipCard}
            className="absolute right-[5px] bottom-[3px] w-[50px] h-[40px] z-[200] cursor-pointer "
          />
        </>
      )}
      <div
        ref={cardRef}
        className="absolute inset-0 w-full h-full"
        style={{
          transform: `rotateY(${isFlipped ? 180 : 0}deg) rotateX(0deg)`,
          transformStyle: "preserve-3d",
          WebkitTransformStyle: "preserve-3d",
          transition: "transform 600ms ease-in-out",
        }}
      >
          <div
            className="absolute w-full h-full bg-black shadow"
            style={cardSideStyles}
          >
            <div className="absolute top-1 left-1 bottom-[24%] right-1 overflow-hidden z-[100]">
              <div
                className="absolute"
                style={{
                  top: `calc(-35% + ${imageOffset.y}px)`,
                  left: `calc(-20% + ${imageOffset.x}px)`,
                  bottom: `calc(-10% - ${imageOffset.y}px)`,
                  right: `calc(-20% - ${imageOffset.x}px)`,
                  transition:
                    imageOffset.x === 0 && imageOffset.y === 0
                      ? "all 300ms ease-out"
                      : "none",
                  pointerEvents: "none",
                }}
              >
                <CardImage
                  x={x}
                  y={y}
                  offset={imageOffset}
                  imageGeneratedAt={
                    hexagenInfo
                      ? (hexagenInfo.regeneratedAt || hexagenInfo.createdAt)
                      : undefined
                  }
                  imageVersion={hexagenInfo?.cardVersion}
                />
              </div>
              {!loading && videoStatus === "READY" && showVideo && (
                <video
                  key={`card-video-${x}-${y}`}
                  ref={videoRef}
                  muted
                  playsInline
                  autoPlay
                  loop
                  preload="auto"
                  src={videoFromCoords(x, y, hexagenInfo?.videoVersion ?? undefined)}
                  className="absolute inset-0 w-full h-full object-cover z-[100000000] transition-opacity duration-300"
                  style={{ opacity: isPopupVideoPlaying ? 1 : 0 }}
                  onPlay={() => setIsPopupVideoPlaying(true)}
                  onError={(e) => {
                    (e.currentTarget as HTMLVideoElement).style.display = "none";
                  }}
                />
              )}
            </div>

            <div className="absolute bottom-3 left-[5%] right-[5%] h-[20%] flex items-center justify-center text-center">
              {loading ? (
                <div className="leading-tight text-gray-500 text-[17px] min-h-[2.5em] flex items-center justify-center">
                  <span className="animate-pulse">PROMPT...</span>
                </div>
              ) : (
                <div
                  className="leading-tight"
                  style={{
                    fontSize: promptText.length > 80 ? "15px" : promptText.length > 60 ? "16px" : promptText.length > 40 ? "17px" : "18px",
                  }}
                >
                  {promptText.slice(0, promptVisibleLength)}
                </div>
              )}
            </div>

            <div className="absolute left-0 top-0 py-2 px-3 bg-black text-sm select-none z-[1000000000]">
              {loading ? (
                <div className="select-none" aria-hidden>Loading...</div>
              ) : (
                <div className="cursor-pointer hover:underline" onClick={clickOnOwner}>
                  {hexagenExtra?.owner?.name || hexagenInfo!.owner}
                </div>
              )}
            </div>

            <CloseButton />
            <FlipButton />

            <div className={`absolute bottom-[146px] left-0 right-0 flex justify-center flex-wrap gap-4 z-[1000000000] ${loading ? "pointer-events-none" : ""}`}>
              <div
                className="px-4 py-1 pb-2 text-xs select-none border border-white rounded bg-black flex items-center justify-center gap-1 cursor-pointer hover:bg-white hover:text-black transition-colors min-w-0"
                onClick={loading ? undefined : handleDownload}
                title={loading ? undefined : (videoStatus === "READY" && showVideo ? "Download video" : "Download image")}
              >
                <Icon icon={FaDownload} className="text-[14px] translate-y-[-1px] shrink-0" />
                <span>Download</span>
              </div>
              {loading && (
                <>
                  <div className="px-4 py-1 pb-2 text-xs select-none border border-white rounded bg-black flex items-center justify-center gap-1 min-w-[5.5rem]">
                    <Icon icon={RiVideoAddLine} className="text-[14px] translate-y-[1px] shrink-0" />
                    <span>Animate!</span>
                  </div>
                  <div className="px-4 py-1 pb-2 text-xs select-none border border-white rounded bg-black flex items-center justify-center gap-1 min-w-[5.5rem]">
                    <Icon icon={GoHeartFill} className="text-[14px] translate-y-[2px] shrink-0" />
                    <span>upvote | …</span>
                  </div>
                </>
              )}
              {!loading && (
                <>
                  {(videoStatus === "NONE" || videoStatus === "FAILED") && (
                    <div
                      className={`px-4 py-1 pb-2 text-xs select-none border rounded transition-colors flex items-center justify-center gap-1 min-w-[5.5rem] ${hasAllNeighbors ? "rainbow-hover cursor-pointer border-white bg-black hover:bg-white hover:text-black" : "cursor-pointer border-gray-600 bg-black text-gray-500"}`}
                      onClick={handleAnimate}
                      title={hasAllNeighbors ? undefined : "All 6 neighbors required"}
                    >
                      <Icon icon={RiVideoAddLine} className="text-[14px] translate-y-[1px] shrink-0" />
                      <span>Animate!</span>
                    </div>
                  )}
                  {videoStatus === "GENERATING" && (
                    <div className="min-w-[5.5rem] flex justify-center">
                      <GeneratingButton />
                    </div>
                  )}
                  {videoStatus === "READY" && (
                    <div
                      className="px-4 py-1 pb-2 text-xs select-none cursor-pointer border border-white rounded bg-black hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-1 min-w-[5.5rem]"
                      onClick={() => setShowVideo(!showVideo)}
                    >
                      <span>{showVideo ? "Show Image" : "Show Video"}</span>
                    </div>
                  )}
                  <div
                    className="px-4 py-1 pb-2 text-xs select-none cursor-pointer border border-white rounded bg-black hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-1 min-w-[5.5rem]"
                    onClick={handleUpvote}
                  >
                    <Icon
                      icon={GoHeartFill}
                      className={`text-[14px] translate-y-[2px] shrink-0 ${hasUpvote ? "text-red-500" : ""} ${upvoteAnimation ? "custom-pulse" : ""}`}
                    />
                    <span>upvote | {hexagenInfo!.upvotes}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div
            className="absolute w-full h-full bg-black shadow flex items-center justify-center"
            style={{
              ...cardSideStyles,
              transform: enableCardTilt
                ? `rotateY(180deg) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
                : "rotateY(180deg)",
            }}
          >
            <div className="text-center px-2">
              <h3 className="text-2xl mb-1">
                Hexagen info
                {!loading && (
                  <span
                    className="text-xs ml-2 border border-white rounded px-1 inline-block"
                    style={{ transform: "translateY(-4px)" }}
                  >
                    {hexagenInfo!.isInDeck ? "Player" : "Ai"}
                  </span>
                )}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="pt-2 pb-1 text-xs flex items-center justify-center gap-2">
                  <span className="text-[35px]">{loading ? "…" : hexagenInfo!.points}</span>
                  <div className="text-left">
                    <div>creativity</div>
                    <div>score</div>
                  </div>
                </div>
                <div>x: {x} | y: {y}</div>
                {loading ? (
                  <div>author: …</div>
                ) : (
                  <div>
                    author:{" "}
                    <span className="cursor-pointer underline" onClick={clickOnCreator}>
                      {hexagenExtra?.creator?.name || hexagenInfo!.creator}
                    </span>
                  </div>
                )}
                {loading ? (
                  <div>owner: …</div>
                ) : (
                  <div>
                    owner:{" "}
                    <span className="cursor-pointer underline" onClick={clickOnOwner}>
                      {hexagenExtra?.owner?.name || hexagenInfo!.owner}
                    </span>
                  </div>
                )}
                <div>views: {loading ? "…" : hexagenInfo!.views}</div>
                <div>upvotes: {loading ? "…" : hexagenInfo!.upvotes}</div>
                {!loading && (
                  <div className="pb-2">
                    created: {getTimeAgo(new Date(hexagenInfo!.createdAt).toISOString())}
                  </div>
                )}

              <div className="pt-3">
                <div
                  className="px-4 py-1 text-xs select-none cursor-pointer border border-white rounded bg-black hover:bg-white hover:text-black transition-colors inline-block"
                  onClick={loading ? undefined : clickOnOpenComments}
                >
                  comments | {loading ? "…" : hexagenInfo!.commentsNum}
                </div>
              </div>

                {!loading && state.player && (state.player.role === "admin" || isPlayerOwner) && (
                  <div className="flex justify-center gap-4 pt-2 flex-wrap">
                    <div
                      className="text-amber-500 cursor-pointer hover:text-amber-400 flex items-center gap-1 disabled:opacity-50"
                      onClick={isRegenerating ? undefined : regenerateHex}
                    >
                      <Icon icon={LuRefreshCcw} className="text-[16px]" />
                      <span className="text-sm">{isRegenerating ? "…" : state.player.role === "admin" ? "Regen · free" : "Regen · 25⚡"}</span>
                    </div>
                    {hexagenInfo.videoStatus === "READY" && (
                      <div
                        className="text-cyan-500 cursor-pointer hover:text-cyan-400 flex items-center gap-1 disabled:opacity-50"
                        onClick={isRegeneratingVideo ? undefined : regenerateVideo}
                      >
                        <Icon icon={RiVideoAddLine} className="text-[16px]" />
                        <span className="text-sm">{isRegeneratingVideo ? "…" : state.player.role === "admin" ? "Regen video · free" : "Regen video · 10🌟"}</span>
                      </div>
                    )}
                    {state.player.role === "admin" && (
                      <div
                        className="text-red-600 cursor-pointer hover:text-red-800 flex items-center gap-1"
                        onClick={deleteHex}
                      >
                        <Icon icon={RiDeleteBin6Line} className="text-[16px]" />
                        <span className="text-sm">Delete</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-center pt-2">
                  {!loading && showAddToDeckButton && (
                    <div
                      className="underline cursor-pointer hover:no-underline"
                      onClick={addToDeck}
                    >
                      Add to deck
                    </div>
                  )}
                </div>
              </div>
            </div>

            <CloseButton />
            <FlipButton />
          </div>
      </div>
      {isCommentsOpen && (
        <div className="absolute inset-0 bg-black p-4 z-[1000000040] overflow-y-auto">
          <div
            onClick={clickOnCloseComments}
            className="select-none cursor-pointer border border-white rounded px-2 py-1 text-xs mb-4 inline-block hover:bg-white hover:text-black transition-colors"
          >
            {"<<< "}back
          </div>
          <Comments x={x} y={y} />
        </div>
      )}
      {isAnimateOpen && (
        <div className="absolute inset-0 z-[1000000040]">
          <div className="absolute inset-0" style={{ animation: "rainbow 4s linear infinite" }} />
          <div className="absolute inset-[7px] bg-black p-4 overflow-y-auto">
            <div
              onClick={() => setIsAnimateOpen(false)}
              className="select-none cursor-pointer border border-white rounded px-2 py-1 text-xs mb-4 inline-block hover:bg-white hover:text-black transition-colors"
            >
              {"<<< "}back
            </div>

            <div className="max-w-md mx-auto text-center pt-24 px-10">
              <h2 className="text-xl mb-4">Generate AI Video</h2>
              {!canAffordVideo ? (
                <>
                  <div className="text-sm text-gray-300 mb-6">
                    Not enough Sparks. You need 10 Sparks to generate a short video from this image.
                  </div>
                  <div className="text-sm text-gray-300">
                    Sparks purchases are no longer available.
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm text-gray-300 mb-2">
                    Get ready:<br /> AI will animate this hexagon!
                    <br /><br />This is an experimental feature — results may be surprising and unpredictable.
                    <br /><br /><b className="text-white">Cost: {videoCost} 🌟 Sparks</b><br /><br /></div>
                  <div
                    className="rainbow-hover w-fit px-4 py-1 text-xs select-none cursor-pointer border border-white rounded bg-black hover:bg-white hover:text-black transition-colors inline-block"
                    onClick={handleConfirmAnimate}
                  >
                    Start generation
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {attackData && (
        <div className="absolute inset-0 bg-black p-4 z-[1000000040] overflow-y-auto">
          <div
            onClick={clickOnCloseAttackData}
            className="select-none cursor-pointer border border-white rounded px-2 py-1 text-xs mb-4 inline-block hover:bg-white hover:text-black transition-colors absolute top-4 left-4"
          >
            {"<<< "}
          </div>

          <div className="max-w-md mx-auto">
            <h2 className="text-xl text-center mb-6">Attack Confirmation</h2>

            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-700 rounded p-3">
                  <div className="text-gray-400 text-xs mb-1">
                    ATTACKER (YOU)
                  </div>
                  <div className="font-semibold">
                    {state.player?.name || "Unknown Player"}
                  </div>
                  <div className="text-xs text-gray-400">
                    Rating: {state.player?.rarityRating || 0}
                  </div>
                </div>

                <div className="border border-gray-700 rounded p-3">
                  <div className="text-gray-400 text-xs mb-1">DEFENDER</div>
                  <div className="font-semibold">
                    {hexagenExtra?.owner?.name || "Unknown Owner"}
                  </div>
                  <div className="text-xs text-gray-400">
                    Rating: {hexagenExtra?.owner?.rarityRating || 0}
                  </div>
                </div>
              </div>

              <div className="border border-gray-700 rounded p-3">
                <div className="text-gray-400 text-xs mb-1">TARGET CARD</div>
                <div className="font-semibold">{cardName}</div>
                <div className="text-xs text-gray-400">
                  Rarity: {attackData.capturedCardRarity}
                </div>
                <div className="text-xs text-gray-400">
                  Position: ({x}, {y})
                </div>
              </div>

              <div className="border border-gray-700 rounded p-3">
                <div className="text-gray-400 text-xs mb-1">BATTLE STATS</div>
                <div className="flex justify-between items-center">
                  <span>Win chance:</span>
                  <span
                    className={`font-bold ${
                      attackData.chanceToWin >= 50
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                  >
                    {attackData.chanceToWin}%
                  </span>
                </div>
              </div>

              {attackData.playerDeckSize >= MAX_DECK_SIZE &&
                attackData.removedCard && (
                  <div className="border border-gray-700 rounded p-3">
                    <div className="text-gray-400 text-xs mb-1">
                      DECK CHANGES
                    </div>
                    <div className="text-xs">
                      <div className="text-red-400 mb-1">
                        Will remove:{" "}
                        {attackData.removedCard.headline.replace(/\.$/, "")}{" "}
                        (rarity: {attackData.removedCard.rarityScore})
                      </div>
                      <div className="text-green-400">
                        Will add: {cardName} (rarity:{" "}
                        {attackData.capturedCardRarity})
                      </div>
                    </div>
                  </div>
                )}

              <div className="border border-gray-700 rounded p-3">
                <div className="text-gray-400 text-xs mb-1">
                  DECK IMPROVEMENT
                </div>
                <div className="flex justify-between items-center">
                  <span>New rarity score:</span>
                  <span className="font-semibold">
                    {attackData.newMediumRarityScore?.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Change:</span>
                  <span
                    className={`font-bold ${
                      attackData.isImproved ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {attackData.isImproved ? "+" : ""}
                    {attackData.improvement?.toFixed(1)} (
                    {attackData.isImproved ? "+" : ""}
                    {attackData.improvementPercent}%)
                  </span>
                </div>
              </div>

              <div
                className={`border rounded p-3 ${
                  attackData.isReasonable
                    ? "border-green-600 bg-green-900/20"
                    : "border-red-600 bg-red-900/20"
                }`}
              >
                <div className="text-center font-semibold">
                  {attackData.isReasonable
                    ? "Recommended Attack"
                    : "Not Recommended"}
                </div>
                <div className="text-xs text-center mt-1 text-gray-400">
                  {attackData.isReasonable
                    ? attackData.chanceToWin >= 50
                      ? "Good chances and deck improvement"
                      : "Deck improvement outweighs the risk"
                    : attackData.chanceToWin < 30
                    ? "Win chance is too low"
                    : attackData.isImproved
                    ? "Risk is too high for this improvement"
                    : "This card will weaken your deck"}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-4">
              <div
                className="border border-gray-600 rounded px-6 py-2 hover:bg-gray-800 select-none cursor-pointer inline-block"
                onClick={clickOnCloseAttackData}
              >
                Cancel
              </div>
              <div
                className="border border-white rounded px-6 py-2 hover:bg-white hover:text-black select-none cursor-pointer inline-block font-semibold"
                onClick={clickOnConfirmAttack}
              >
                <Icon
                  icon={GiCrossedSwords}
                  className="mr-2 inline text-[18px] translate-y-[-1px]"
                />
                Confirm Attack
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
